import React, { useState, useEffect } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Input,
  Button,
  Box,
  Heading,
  Text,
  useToast,
  Select,
  InputGroup,
  InputRightElement,
} from "@chakra-ui/react";
import { useForm, Controller } from "react-hook-form";
import { useAddFamilyMemberStore } from "../store";
import { useAddFamilyMemberToUserMutation } from "../graphql/types-and-hooks";
import dayjs from "dayjs";
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import { servicebookingFlowStore } from "../store/ServiceBookingFlowStore";
import DateOfBirthSelect from './DateOfBirthSelect';
import { observer } from "mobx-react-lite"; // Import observer from mobx-react-lite
import * as PatientService from "../services/PatientService"; // Adjust the import path as necessary

interface CreateFamilyMemberProps {
  isOpen: boolean;
  onClose: () => void;
}

interface AddFamilyMemberPersonalDetailsData {
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  gender: string;
  mobileNumber: string;
}

const CreateFamilyMember: React.FC<CreateFamilyMemberProps> = observer(({ isOpen, onClose }) => {
  const toast = useToast();

  const [personalDetails, setData] = useAddFamilyMemberStore(
    (state) => [state.personalDetails, state.setData, state.resetData]
  );

  useEffect(() => {
    if (isOpen) {
        // Send a message to the parent window to scroll to the top
        window.parent.postMessage({ action: 'scrollToTop' }, '*');
    }
  }, [isOpen]);

  // Access store within the observer component
  const isServiceBooking = servicebookingFlowStore.isServiceBooking;
  const buttonBgColor = isServiceBooking 
    ? servicebookingFlowStore?.configurationDetails?.colorScheme?.buttonBg 
    : "#B92F2F";

  const { control, handleSubmit, reset, formState: { errors } } = useForm<AddFamilyMemberPersonalDetailsData>({
    defaultValues: {
      firstName: personalDetails?.firstName || "",
      lastName: personalDetails?.lastName || "",
      dateOfBirth: personalDetails?.dateOfBirth || null,
      gender: personalDetails?.gender || "",
      mobileNumber: personalDetails?.mobileNumber || "",
    },
  });

  const [
    addFamilyMemberToUserMutation,
    { loading: addFamilyMemberToUserLoading },
  ] = useAddFamilyMemberToUserMutation({
    refetchQueries: ["User", "UpcomingAppointments"],
    awaitRefetchQueries: true,
  });

  const [isMfaModalOpen, setIsMfaModalOpen] = useState(false);
  const [mfaCode, setMfaCode] = useState("");

  const onSubmit = handleSubmit(async (data: AddFamilyMemberPersonalDetailsData) => {
    const { firstName, lastName, dateOfBirth, gender, mobileNumber } = data;

    const formattedData = {
      ...data,
      dateOfBirth: data.dateOfBirth ? dayjs(data.dateOfBirth).format('DD/MM/YYYY') : null
    };

    // Basic validation
    if (!firstName || !lastName || !dateOfBirth || !gender || !mobileNumber) {
      toast({
        title: "Error",
        description: "All fields are required",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    try {
      setData({
        personalDetails: data,
      });
      
      await PatientService.sendFamilyMemberVerification(data.mobileNumber);

      setData({
        personalDetails: formattedData,
      });

      setIsMfaModalOpen(true); // Open the MFA modal
    } catch {
      toast({
        title: "Error",
        description: "Failed to create family member",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  });

  const handleMfaSubmit = async () => {
    try {
      await addFamilyMemberToUserMutation({
        variables: {
          input: {
            first_name: personalDetails.firstName,
            last_name: personalDetails.lastName,
            dob: personalDetails.dateOfBirth,
            gender: personalDetails.gender,
            phone_number: personalDetails.mobileNumber,
            code: mfaCode,
          },
        },
      });

      toast({
        title: "Success",
        description: "MFA code verified successfully",
        status: "success",
        duration: 5000,
        isClosable: true,
      });

      setIsMfaModalOpen(false); // Close the MFA modal
      reset(); // Reset the form after successful submission
      onClose(); // Close the main modal
    } catch (error) {
      if (
        !["VERIFICATION_CODE_MISMATCH", "VERIFICATION_CODE_EXPIRED"]?.includes(
          error?.graphQLErrors?.[0]?.extensions?.code
        )
      ) {
        console.log(error);
      }

      toast({
        title: "Error",
        description: "Invalid MFA code",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} size="md" blockScrollOnMount={false} closeOnOverlayClick={false}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Enter Personal Details</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Box flex="1" p={4}>
              <Heading as="h2" size="md" noOfLines={1}>
                Enter Personal Details
              </Heading>
              <Text fontSize="md">
                Input the following details to proceed with sign up
              </Text>
              <form onSubmit={onSubmit}>
                <FormControl mb={4} mt={8} isInvalid={Boolean(errors.firstName)}>
                  <FormLabel>First Name</FormLabel>
                  <Controller
                    name="firstName"
                    control={control}
                    rules={{ required: "First name is required" }}
                    render={({ field }) => (
                      <Input
                        {...field}
                        placeholder="Enter your First Name"
                        borderRadius="30px"
                      />
                    )}
                  />
                  {errors.firstName && (
                    <Text color="red.500" fontSize="sm">
                      {errors.firstName.message}
                    </Text>
                  )}
                </FormControl>

                <FormControl mb={4} mt={8} isInvalid={Boolean(errors.lastName)}>
                  <FormLabel>Last Name</FormLabel>
                  <Controller
                    name="lastName"
                    control={control}
                    rules={{ required: "Last name is required" }}
                    render={({ field }) => (
                      <Input
                        {...field}
                        placeholder="Enter your Last Name"
                        borderRadius="30px"
                      />
                    )}
                  />
                  {errors.lastName && (
                    <Text color="red.500" fontSize="sm">
                      {errors.lastName.message}
                    </Text>
                  )}
                </FormControl>

                <FormControl mb={4} mt={8} isInvalid={Boolean(errors.dateOfBirth)}>
                  <FormLabel>Date of Birth</FormLabel>
                  <Controller
                    name="dateOfBirth"
                    control={control}
                    rules={{ required: "Date of birth is required" }}
                    render={({ field }) => (
                      <DateOfBirthSelect value={field.value} onChange={field.onChange} />
                    )}
                  />
                  {errors.dateOfBirth && (
                    <Text color="red.500" fontSize="sm">
                      {errors.dateOfBirth.message}
                    </Text>
                  )}
                </FormControl>

                <FormControl mb={4} mt={8} isInvalid={Boolean(errors.gender)}>
                  <FormLabel>Gender</FormLabel>
                  <Controller
                    name="gender"
                    control={control}
                    rules={{ required: "Gender is required" }}
                    render={({ field }) => (
                      <Select
                        {...field}
                        placeholder="Select Gender"
                        borderRadius="30px"
                        color={'gray'}
                      >
                        <option value="male">Male</option>
                        <option value="female">Female</option>
                        <option value="other">Other</option>
                      </Select>
                    )}
                  />
                  {errors.gender && (
                    <Text color="red.500" fontSize="sm">
                      {errors.gender.message}
                    </Text>
                  )}
                </FormControl>

                <FormControl mb={4} mt={8} isInvalid={Boolean(errors.mobileNumber)}>
                  <FormLabel>Mobile Number</FormLabel>
                  <Controller
                    name="mobileNumber"
                    control={control}
                    rules={{
                      required: "Mobile number is required",
                      validate: (value) => {
                        const phoneNumber = parsePhoneNumberFromString(value, 'AU'); 
                        return phoneNumber && phoneNumber.isValid()
                            ? true
                            : "Invalid phone number format";
                    },
                    }}
                    render={({ field: { onChange, value, ...rest } }) => (
                      <InputGroup>
                          <Input
                              {...rest}
                              value={value}
                              borderRadius={30}
                              placeholder="Enter your Phone Number"
                              onBlur={(e) => {
                                  const input = e.target.value;
                                  const phoneNumber = parsePhoneNumberFromString(input, 'AU');
                                  if (phoneNumber && phoneNumber.isValid()) {
                                      onChange(phoneNumber.formatInternational());
                                  } else {
                                      onChange(input);
                                  }
                              }}
                              onChange={onChange}
                          />
                          <InputRightElement>
                              <span role="img" aria-label="Australian Flag" style={{ fontSize: '1.5rem' }}>
                                  🇦🇺
                              </span>
                          </InputRightElement>
                      </InputGroup>
                  )}
                  />
                  {errors.mobileNumber && (
                    <Text color="red.500" fontSize="sm">
                      {errors.mobileNumber.message}
                    </Text>
                  )}
                </FormControl>

                <Button
                  type="submit"
                  bg={buttonBgColor}
                  color="#fff"
                  mt={5}
                  width="100%"
                  borderRadius="30px"
                  mb="20px"
                >
                  Create a Family Member
                </Button>
              </form>
            </Box>
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* MFA Code Modal */}
      <Modal isOpen={isMfaModalOpen} onClose={() => setIsMfaModalOpen(false)} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Enter MFA Code</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Box flex="1" p={4}>
              <FormControl mb={4} mt={8}>
                <FormLabel>We have sent a verification code to a mobile number you entered</FormLabel>
                <Input
                  placeholder="Enter MFA Code"
                  borderRadius="30px"
                  value={mfaCode}
                  onChange={(e) => setMfaCode(e.target.value)}
                />
              </FormControl>
              <Button
                onClick={handleMfaSubmit}
                bg={buttonBgColor}
                color="#fff"
                mt={5}
                width="100%"
                borderRadius="30px"
                mb="20px"
                isLoading={addFamilyMemberToUserLoading}
              >
                Verify MFA Code
              </Button>
            </Box>
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
});

export default CreateFamilyMember;